import * as React from 'react'
import { CheckCircle2, Loader2, Smartphone } from 'lucide-react'
import {
  useCreateDevicePairingMutation,
  useRevokeDevicePairingMutation,
  useGetDevicePairingQuery,
  useListDevicePairingsQuery,
  type EntitiesCreateDevicePairingResponse,
  type DevicePairingStatus,
  type EntitiesPairedDeviceResponse
} from '@axilon-apps/domains-identity/state'
import {
  getIdentityApiUrl,
  getOntologyServiceBaseUrl,
  getProxyBaseUrl,
  isIdentityEnabled
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../../../components/ui/button'
import { Card, CardContent } from '../../../../components/ui/card'
import { Separator } from '../../../../components/ui/separator'
import { formatRelativeTime } from '../../../../lib/time'
import { ConfirmDialog } from '../../../ConfigurePage/components/DeleteConfirmDialog'
import { useNow } from '../../../RunPage/useNow'
import { describeIssuer } from './describe-issuer'
import { devicePairingErrorMessage } from './device-pairing-error'
import { buildPairingQrPayload, PairingPayloadError } from './pairing-payload'
import { effectivePairingStatus, formatPairingCountdown, isPairingInFlight } from './pairing-status'
import { QrCodeSvg } from './QrCodeSvg'

const POLL_INTERVAL_MS = 2_000
const CLOCK_TICK_MS = 500

/** axilon-api-service root: the configured base without the `/api/ontology` suffix. */
function apiServiceBaseUrl(): string {
  return getOntologyServiceBaseUrl().replace(/\/api\/ontology$/, '')
}

interface MobileAppCardProps {
  /** Scroll the card into view on mount (`?tab=mobile-app` deep link). */
  focus?: boolean
}

/**
 * Settings → General: pair the Prophet Android app by QR code. Only shown when
 * the identity service is enabled — pairing mints an identity session.
 */
export function MobileAppCard({ focus = false }: MobileAppCardProps) {
  return isIdentityEnabled() ? <MobileAppPairingCard focus={focus} /> : null
}

function MobileAppPairingCard({ focus }: { focus: boolean }) {
  const sectionRef = React.useRef<HTMLElement>(null)
  React.useEffect(() => {
    if (focus) sectionRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })
  }, [focus])

  const devices = useListDevicePairingsQuery()
  const [createPairing, createState] = useCreateDevicePairingMutation()
  const [revokePairing, revokeState] = useRevokeDevicePairingMutation()

  const [pairing, setPairing] = React.useState<EntitiesCreateDevicePairingResponse | null>(null)
  const [cancelled, setCancelled] = React.useState(false)
  const [stopPolling, setStopPolling] = React.useState(false)
  const [error, setError] = React.useState('')
  const [revoking, setRevoking] = React.useState<EntitiesPairedDeviceResponse | null>(null)
  const [revokeError, setRevokeError] = React.useState('')

  const pairingQuery = useGetDevicePairingQuery(pairing?.id ?? '', {
    skip: !pairing,
    pollingInterval: pairing && !stopPolling ? POLL_INTERVAL_MS : 0
  })
  const serverPairing =
    pairing && pairingQuery.currentData?.id === pairing.id ? pairingQuery.currentData : null

  const clockActive = Boolean(pairing) && !cancelled && !stopPolling
  const now = useNow(clockActive, CLOCK_TICK_MS)
  const status: DevicePairingStatus | null = !pairing
    ? null
    : cancelled
      ? 'revoked'
      : effectivePairingStatus(serverPairing?.status ?? 'pending', pairing.expiresAt, now)

  React.useEffect(() => {
    if (status && !isPairingInFlight(status)) setStopPolling(true)
  }, [status])

  const { refetch: refetchDevices } = devices
  React.useEffect(() => {
    if (status === 'confirmed') void refetchDevices()
  }, [status, refetchDevices])

  const qr = React.useMemo((): { payload: string } | { error: string } | null => {
    if (!pairing) return null
    try {
      return {
        payload: buildPairingQrPayload({
          identityUrl: getIdentityApiUrl(),
          apiServiceUrl: apiServiceBaseUrl(),
          proxyBaseUrl: getProxyBaseUrl(),
          pairingId: pairing.id,
          secret: pairing.secret,
          origin: window.location.origin
        })
      }
    } catch (err) {
      return {
        error:
          err instanceof PairingPayloadError
            ? err.message
            : 'The pairing code could not be built for this deployment.'
      }
    }
  }, [pairing])

  const startPairing = async () => {
    setError('')
    try {
      const created = await createPairing({ issuerLabel: describeIssuer() }).unwrap()
      setCancelled(false)
      setStopPolling(false)
      setPairing(created)
    } catch (err) {
      setError(devicePairingErrorMessage(err, 'Could not start pairing. Try again.'))
    }
  }

  const cancelPairing = async () => {
    if (!pairing) return
    setError('')
    try {
      await revokePairing(pairing.id).unwrap()
      setCancelled(true)
    } catch (err) {
      setError(devicePairingErrorMessage(err, 'Could not cancel pairing. Try again.'))
    }
  }

  const confirmRevoke = async () => {
    if (!revoking) return
    setRevokeError('')
    try {
      await revokePairing(revoking.id).unwrap()
      setRevoking(null)
    } catch (err) {
      setRevokeError(devicePairingErrorMessage(err, 'Could not revoke the device. Try again.'))
    }
  }

  const inFlight = status !== null && isPairingInFlight(status)
  const pairedDevices = devices.currentData?.devicePairings ?? []

  const startLabel =
    status === null
      ? 'Pair a device'
      : status === 'confirmed'
        ? 'Pair another device'
        : 'Generate a new code'

  return (
    <section ref={sectionRef} aria-labelledby="mobile-app-connection-title" className="scroll-mt-4">
      <Card className="gap-0 overflow-hidden py-0 shadow-sm">
        <CardContent className="flex items-start justify-between gap-4 px-4 py-3.5">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
              <Smartphone className="size-4" strokeWidth={1.75} />
            </div>
            <div className="min-w-0">
              <h2 id="mobile-app-connection-title" className="m-0 text-sm font-semibold text-ink">
                Mobile app connection
              </h2>
              <p className="mt-0.5 text-xs text-ink-3">
                Pair the Prophet Android app by scanning a code — no password is typed on the phone.
                Codes expire after 2 minutes and work once.
              </p>
            </div>
          </div>
          {!inFlight ? (
            <Button
              type="button"
              size="sm"
              className="shrink-0"
              onClick={() => void startPairing()}
              disabled={createState.isLoading}
            >
              {createState.isLoading ? <Loader2 className="size-3.5 animate-spin" /> : null}
              {startLabel}
            </Button>
          ) : null}
        </CardContent>

        {error ? (
          <CardContent className="px-4 pb-3.5 pt-0">
            <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
              {error}
            </div>
          </CardContent>
        ) : null}

        {pairing && status ? (
          <>
            <Separator />
            <CardContent className="flex flex-col items-center gap-3 px-4 py-4 sm:flex-row sm:items-start sm:gap-6">
              {inFlight ? (
                qr && 'payload' in qr ? (
                  <div className="shrink-0">
                    <QrCodeSvg value={qr.payload} label="Pairing QR code for the Prophet app" />
                  </div>
                ) : (
                  <div
                    role="alert"
                    className="max-w-56 rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down"
                  >
                    {qr && 'error' in qr ? qr.error : 'The pairing code is unavailable.'}
                  </div>
                )
              ) : null}
              <div className="flex min-w-0 flex-col gap-2 text-center sm:text-left">
                {inFlight ? (
                  <>
                    <div>
                      <div className="text-xs text-ink-3">Match code</div>
                      <div
                        className="font-mono text-3xl font-semibold tracking-[0.3em] text-ink"
                        aria-label={`Match code ${pairing.matchCode.split('').join(' ')}`}
                      >
                        {pairing.matchCode}
                      </div>
                      <p className="mt-0.5 text-xs text-ink-3">
                        Confirm this code matches the one shown on your phone.
                      </p>
                    </div>
                    <div className="text-xs text-ink-3">
                      Expires in{' '}
                      <time className="font-mono text-ink-2" dateTime={pairing.expiresAt}>
                        {formatPairingCountdown(pairing.expiresAt, now)}
                      </time>
                    </div>
                  </>
                ) : null}
                <PairingStatusLine status={status} deviceName={serverPairing?.deviceName ?? null} />
                {inFlight ? (
                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void cancelPairing()}
                      disabled={revokeState.isLoading}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : null}
              </div>
            </CardContent>
          </>
        ) : null}

        <Separator />
        <CardContent className="px-4 py-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-ink-3">
            Paired devices
          </div>
          {devices.isLoading ? (
            <p className="mt-1.5 text-xs text-ink-3">Loading paired devices…</p>
          ) : devices.isError ? (
            <div
              role="alert"
              className="mt-1.5 rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down"
            >
              {devicePairingErrorMessage(devices.error, 'Paired devices are unavailable.')}
            </div>
          ) : pairedDevices.length === 0 ? (
            <p className="mt-1.5 text-xs text-ink-3">No devices are paired.</p>
          ) : (
            <ul className="mt-1 flex flex-col divide-y divide-border">
              {pairedDevices.map((device) => (
                <li key={device.id} className="flex items-center justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-ink">
                      {device.deviceName?.trim() || 'Unnamed device'}
                    </div>
                    <div className="text-xs text-ink-3">
                      Paired{' '}
                      {formatRelativeTime(device.confirmedAt, { fallback: 'at an unknown time' })}
                      {' · '}
                      {device.lastUsedAt
                        ? `Last used ${formatRelativeTime(device.lastUsedAt, { fallback: 'unknown' })}`
                        : 'Last used never'}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="xs"
                    className="shrink-0"
                    onClick={() => {
                      setRevokeError('')
                      setRevoking(device)
                    }}
                    aria-label={`Revoke ${device.deviceName?.trim() || 'unnamed device'}`}
                  >
                    Revoke
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {revokeError ? (
            <div
              role="alert"
              className="mt-1.5 rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down"
            >
              {revokeError}
            </div>
          ) : null}
        </CardContent>

        <ConfirmDialog
          open={revoking !== null}
          onOpenChange={(open) => {
            if (!open) setRevoking(null)
          }}
          title="Revoke device?"
          description={`${revoking?.deviceName?.trim() || 'This device'} will be signed out of Prophet and must be paired again to reconnect.`}
          confirmLabel="Revoke"
          destructive
          busy={revokeState.isLoading}
          keepOpenOnConfirm
          onConfirm={() => void confirmRevoke()}
        />
      </Card>
    </section>
  )
}

function PairingStatusLine({
  status,
  deviceName
}: {
  status: DevicePairingStatus
  deviceName: string | null
}) {
  const base = 'flex items-center gap-1.5 text-xs'
  let content: React.ReactNode
  switch (status) {
    case 'pending':
      content = (
        <span className={`${base} text-ink-3`}>
          <Loader2 className="size-3 animate-spin" />
          Waiting for a scan…
        </span>
      )
      break
    case 'claimed':
      content = (
        <span className={`${base} text-ink-2`}>
          Claimed by {deviceName?.trim() || 'a device'}. Confirm the match code on the phone to
          finish.
        </span>
      )
      break
    case 'confirmed':
      content = (
        <span className={`${base} font-medium text-st-run`}>
          <CheckCircle2 className="size-3.5" />
          Device paired
        </span>
      )
      break
    case 'expired':
      content = <span className={`${base} text-st-down`}>Code expired</span>
      break
    default:
      content = <span className={`${base} text-ink-3`}>Pairing cancelled</span>
  }
  return (
    <div role="status" aria-live="polite">
      {content}
    </div>
  )
}
